import {
  type MaybeRefOrGetter,
  nextTick,
  toValue,
  useTemplateRef,
  watch,
} from 'vue';
import type { PortraitLike } from './useParallelPortraitSlots';

export interface PortraitGalleryHandle {
  canScroll: () => boolean;
  isItemVisible: (uuid: string) => boolean;
  scrollToItem: (uuid: string) => void;
}

interface UsePortraitGalleryAdapterOptions {
  displayPageSize: MaybeRefOrGetter<number>;
  displaySlots: MaybeRefOrGetter<readonly PortraitLike[]>;
  getDisplaySlotRevealOrder: () => number[];
  getPreloadedPortraits: () => readonly PortraitLike[];
  isDisposed: () => boolean;
  isParallelDisplaySlotsEnabled: MaybeRefOrGetter<boolean>;
  portraitDisplaySyncSignature: MaybeRefOrGetter<string>;
  settledDisplaySlotPrefixCount: MaybeRefOrGetter<number>;
  syncDisplaySlotsFromCurrentState: () => void;
}

export interface PortraitGalleryAdapter {
  scrollToNextSlideIfNeeded: () => void;
}

export function usePortraitGalleryAdapter(
  options: UsePortraitGalleryAdapterOptions
): PortraitGalleryAdapter {
  const gallery = useTemplateRef<PortraitGalleryHandle>('gallery');
  let lastScrolledSlide = 0;

  function scrollToHiddenPortrait(uuid: string, slide: number): void {
    const galleryHandle = gallery.value;
    if (!galleryHandle?.canScroll() || galleryHandle.isItemVisible(uuid)) {
      return;
    }

    galleryHandle.scrollToItem(uuid);
    lastScrolledSlide = slide;
  }

  function scrollToNextParallelSlide(): void {
    const settledPortraitPrefixCount = toValue(
      options.settledDisplaySlotPrefixCount
    );
    const pageSize = toValue(options.displayPageSize);
    const currentSlide =
      settledPortraitPrefixCount > 0
        ? Math.floor((settledPortraitPrefixCount - 1) / pageSize)
        : 0;

    if (
      settledPortraitPrefixCount <= pageSize ||
      currentSlide <= lastScrolledSlide
    ) {
      return;
    }

    const revealOrder = options.getDisplaySlotRevealOrder();
    const targetIndex =
      revealOrder[currentSlide * pageSize] ?? currentSlide * pageSize;
    const targetPortrait = toValue(options.displaySlots)[targetIndex];
    if (targetPortrait?.uuid) {
      scrollToHiddenPortrait(targetPortrait.uuid, currentSlide);
    }
  }

  function scrollToNextSequentialSlide(): void {
    const preloadedPortraits = options.getPreloadedPortraits();
    const pageSize = toValue(options.displayPageSize);
    const currentSlide = Math.floor((preloadedPortraits.length - 1) / pageSize);

    if (
      preloadedPortraits.length <= pageSize ||
      currentSlide <= lastScrolledSlide
    ) {
      return;
    }

    const lastPortrait = preloadedPortraits.at(-1);
    if (lastPortrait?.uuid) {
      scrollToHiddenPortrait(lastPortrait.uuid, currentSlide);
    }
  }

  function scrollToNextSlideIfNeeded(): void {
    if (options.isDisposed()) {
      return;
    }

    if (toValue(options.isParallelDisplaySlotsEnabled)) {
      scrollToNextParallelSlide();
      return;
    }

    scrollToNextSequentialSlide();
  }

  watch(
    () => toValue(options.portraitDisplaySyncSignature),
    () => {
      if (
        options.isDisposed() ||
        !toValue(options.isParallelDisplaySlotsEnabled)
      ) {
        return;
      }

      options.syncDisplaySlotsFromCurrentState();
    }
  );

  watch(
    () => toValue(options.settledDisplaySlotPrefixCount),
    (newCount, oldCount) => {
      if (
        options.isDisposed() ||
        !toValue(options.isParallelDisplaySlotsEnabled) ||
        newCount <= oldCount
      ) {
        return;
      }

      void nextTick(() => {
        if (!options.isDisposed()) {
          scrollToNextSlideIfNeeded();
        }
      });
    }
  );

  return { scrollToNextSlideIfNeeded };
}
